import { useEffect } from "react";

import { warmupReplayLoaderWorker } from "@hooks/game/activity/session/replay-loader-worker-client";

import type { ReplayDecision } from "@hooks/common/session/replay-decision-utils";
import type { ReplayScenarioTarget } from "@hooks/game/activity/session/use-session-replay.utils";
import type { ReplayData } from "@Shared/types/ReplayData";

interface UseSessionReplayPrefetchProps {
  currentTab: number;
  userId: string;
  scenarioTargets: ReadonlyArray<ReplayScenarioTarget>;
  decisionsCache: Record<number, ReplayDecision[]>;
  replayDataCache: Record<number, ReplayData>;
  replayDataRequestCache: Record<number, Promise<ReplayData>>;
  decisionsRequestCache: Record<number, Promise<ReplayDecision[] | null>>;
  isReplayNotFoundError: (error: unknown) => boolean;
  loadReplayPayload: (resultId: number, replayUserId: string, onDownloadUrl?: (downloadUrl: string) => void) => Promise<ReplayData>;
  computeReplayDecisions: (data: ReplayData) => Promise<ReplayDecision[] | null>;
  onReplayDownloadUrl?: (resultId: number, downloadUrl: string) => void;
}

const useSessionReplayPrefetch = ({
  currentTab,
  userId,
  scenarioTargets,
  decisionsCache,
  replayDataCache,
  replayDataRequestCache,
  decisionsRequestCache,
  isReplayNotFoundError,
  loadReplayPayload,
  computeReplayDecisions,
  onReplayDownloadUrl,
}: UseSessionReplayPrefetchProps) => {
  useEffect(() => {
    if (!userId || scenarioTargets.length === 0) {
      return;
    }

    let isCancelled = false;

    warmupReplayLoaderWorker();

    const runPrefetchQueue = async () => {
      for (const descriptor of scenarioTargets) {
        if (isCancelled) {
          return;
        }

        if (currentTab > 0 && descriptor.tab === currentTab) {
          continue;
        }

        const { cognitiveResultId: resultId } = descriptor;
        if (!resultId) {
          continue;
        }

        const replayCached = Boolean(replayDataCache[resultId]);
        const decisionsCached = Object.hasOwn(decisionsCache, resultId);

        if (replayCached && decisionsCached) {
          continue;
        }

        try {
          let replayDownloadUrl: string | null = null;
          const data =
            replayDataCache[resultId] ??
            (await (replayDataRequestCache[resultId] ??= loadReplayPayload(resultId, userId, downloadUrl => {
              replayDownloadUrl = downloadUrl;
            }).finally(() => {
              delete replayDataRequestCache[resultId];
            })));
          replayDataCache[resultId] = data;

          if (isCancelled) {
            return;
          }

          if (replayDownloadUrl) {
            onReplayDownloadUrl?.(resultId, replayDownloadUrl);
          }

          if (Object.hasOwn(decisionsCache, resultId)) {
            continue;
          }

          const computedDecisions = await (decisionsRequestCache[resultId] ??= computeReplayDecisions(data).finally(() => {
            delete decisionsRequestCache[resultId];
          }));
          if (isCancelled) {
            return;
          }

          if (computedDecisions) {
            decisionsCache[resultId] = computedDecisions;
          }
        } catch (error) {
          if (isCancelled) {
            return;
          }

          if (isReplayNotFoundError(error)) {
            delete decisionsCache[resultId];
          }
        }
      }
    };

    runPrefetchQueue();

    return () => {
      isCancelled = true;
    };
  }, [
    computeReplayDecisions,
    currentTab,
    decisionsCache,
    decisionsRequestCache,
    isReplayNotFoundError,
    loadReplayPayload,
    onReplayDownloadUrl,
    replayDataCache,
    replayDataRequestCache,
    scenarioTargets,
    userId,
  ]);
};

export default useSessionReplayPrefetch;
